import {
  ApolloClient,
  defaultDataIdFromObject,
  InMemoryCache,
  type TypePolicy,
} from "@apollo/client";
import { type FieldMergeFunction } from "@apollo/client/cache";
import { relayStylePagination } from "@apollo/client/utilities";
import { type SubscriptionBoundSection } from "common/types/federation/supergraphTypes";
import supergraphPossibleTypes from "common/types/federation/supergraphApolloPossibleTypes";
import { getLink } from "./utils";

// Always replace, never accumulate: `accountContactsPrefs` is shared by two
// consumers with conflicting needs at the same cache key (accountId+filters)
// -- the Overview's infinite-scroll `loadMore` and the Tab's discrete
// first/prev/next/last page nav, both of which pass `cursor`. Accumulating
// here made the Tab's page nav append instead of replace (INVC-4395). The
// Overview owns its own accumulation across pages via `fetchMore`'s
// `updateQuery` in useAccountOverview instead of relying on this cache policy.
export const accountContactsPrefsMerge: FieldMergeFunction = (
  _existing: unknown,
  incoming: { contacts?: readonly unknown[]; pageInfo?: Record<string, unknown> },
) => incoming;

const federationTypePolicies: Record<string, TypePolicy> = {
  SubscriptionBoundSection: {
    keyFields: (object) => {
      const { section, promptResponder } = object as SubscriptionBoundSection;
      // section can already be cached by Apollo - it returns a __ref field as a unique
      // identifier instead of the full object in those cases
      // eslint-disable-next-line no-underscore-dangle
      const sectionId = "__ref" in section ? section.__ref : section.id;
      return promptResponder?.contactUuid
        ? `SubscriptionBoundSection:${sectionId}:${promptResponder.contactUuid}`
        : `SubscriptionBoundSection:${sectionId}`;
    },
  },
  ACHDebitLimitDto: {
    keyFields: false, // disable cache since ACHDebitLimitDto doesn't have an id field
  },
  Query: {
    fields: {
      unifiedPortalAccounts: relayStylePagination(["filters", "sort"]),
      accountContactsPrefs: {
        keyArgs: ["accountId", "filters"],
        merge: accountContactsPrefsMerge,
      },
    },
  },
};

const federationCache = new InMemoryCache({
  possibleTypes: supergraphPossibleTypes.possibleTypes,
  dataIdFromObject: defaultDataIdFromObject,
  typePolicies: federationTypePolicies,
});

const federationGatewayLink = getLink("/service/federation/graphql", {
  useTerminatingBatchLink: false,
  includeOutlookLink: false,
  graphqlClientName: "main-federation-gateway",
});

export const federationGatewayClient = new ApolloClient({
  cache: federationCache,
  link: federationGatewayLink,
});
